import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import InsertInvitationOutlinedIcon from "@mui/icons-material/InsertInvitationOutlined";
import { Box, IconButton, ListItemIcon, ListItemText, MenuItem, MenuList } from "@mui/material";
import type { DateTime } from "luxon";
import type React from "react";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../../../components/InHouseTooltip/InHouseTooltip";
import { initialRecurringShiftDataStructure } from "../../../../../../components/RecurringShift/RecurringShift";
import { RecurringShiftPanelWrapper } from "../../../../../../components/RecurringShift/RecurringShiftPanelWrapper";
import { DaysInWeek } from "../../../../../../components/RecurringShift/util/DaysInWeekSelector";
import { useChosenDepartment } from "../../../../../../contexts/ChosenDepartment";
import { useHospitalData } from "../../../../../../contexts/HospitalDataProvider";
import { useFormatPersonName } from "../../../../../../hooks/useNameFormat";
import type { Nurse } from "../../../../../../utils/models";
import { SingleShiftCreationForm } from "./SingleShiftCreation/SingleShiftCreationForm";
import type { SelectedCreationType } from "./useDayCellState";

type Props = {
	date: DateTime;
	onCloseCalled: (createdSingleShift: boolean) => void;
	nurseFromProfile?: Nurse;
	selectedCreationType?: SelectedCreationType;
	setSelectedCreationType: React.Dispatch<React.SetStateAction<SelectedCreationType>>;
};

export const ClickedCellEditor: FC<Props> = ({ date, onCloseCalled, nurseFromProfile, setSelectedCreationType, selectedCreationType }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const formatPersonName = useFormatPersonName();
	// The usual-shift guess counts Staffing's loaded weeks, so it stays on the provider until step 5 moves it to the server.
	const { calculateDefaultTimesAndLocationForStaff } = useHospitalData();
	const isMasterScheduleEditBlocked = chosenDepartment.disable_edit_master_schedule;
	const selectSingleCreationType = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			setSelectedCreationType("single");
		},
		[setSelectedCreationType],
	);

	const selectRecurringCreationType = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			setSelectedCreationType("recurring");
		},
		[setSelectedCreationType],
	);

	const unselectCreationType = useCallback(
		(event?: React.MouseEvent, close?: boolean) => {
			event?.stopPropagation();
			setSelectedCreationType(null);
			if (close) {
				onCloseCalled(false);
			}
		},
		[setSelectedCreationType, onCloseCalled],
	);

	const handleOnRecurringClose = useCallback(
		(_?: boolean) => {
			unselectCreationType(undefined, true);
		},
		[unselectCreationType],
	);

	const handleShiftCreationOnClose = useCallback(() => {
		onCloseCalled(true);
	}, [onCloseCalled]);

	return (
		<Box
			style={{
				maxWidth: "fit-content",
				minWidth: "200px",
				height: "fit-content",
			}}
			sx={{
				p: 1,
			}}
		>
			{!selectedCreationType ? (
				<MenuList sx={{ p: 0 }}>
					<MenuItem data-testid="profile-shift-menu-add-single" onClick={selectSingleCreationType}>
						<ListItemIcon>
							<InsertInvitationOutlinedIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>Add Single Shift</ListItemText>
					</MenuItem>
					<InHouseTooltip title={isMasterScheduleEditBlocked ? t("schedule.template.edit.blocked") : ""} disableHoverListener={!isMasterScheduleEditBlocked}>
						<span>
							<MenuItem data-testid="profile-shift-menu-add-recurring" onClick={selectRecurringCreationType} disabled={isMasterScheduleEditBlocked}>
								<ListItemIcon>
									<EventRepeatOutlinedIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText>Add Recurring Shift</ListItemText>
							</MenuItem>
						</span>
					</InHouseTooltip>
				</MenuList>
			) : (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "start",
						maxWidth: "800px",
						maxHeight: "600px",
					}}
				>
					<IconButton onClick={unselectCreationType}>
						<ArrowBackOutlinedIcon fontSize={"small"} />
					</IconButton>
					{selectedCreationType === "single" && nurseFromProfile && (
						<SingleShiftCreationForm nurseFromProfile={nurseFromProfile} selectedDate={date} onCloseCalled={handleShiftCreationOnClose} />
					)}
					{selectedCreationType === "recurring" && (
						<RecurringShiftPanelWrapper
							isSynergyMS={false}
							shouldAddNewShiftOnOpen={true}
							disableHidePopup={true}
							suggestStaffShiftDefaults={calculateDefaultTimesAndLocationForStaff}
							clickedDate={date}
							clickedNurseId={nurseFromProfile?.id}
							onClose={handleOnRecurringClose}
							renderWithoutModal={false}
							initialRecurringShiftData={{
								...initialRecurringShiftDataStructure,
								selectedNurse: nurseFromProfile
									? {
											value: nurseFromProfile,
											label: formatPersonName(nurseFromProfile),
										}
									: null,
								selectedDays: [Object.values(DaysInWeek)[date.weekday % 7]],
							}}
						/>
					)}
				</Box>
			)}
		</Box>
	);
};
